import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { Input } from "./ui/input";
import { ChevronsUpDown, Check, X, Plus } from "lucide-react";

/**
 * Combobox with free-text fallback for the Book "Category" field.
 * - Fetches existing categories from /book/categories on open.
 * - Filters as the user types.
 * - Lets the user click a suggestion OR keep typing a brand-new category.
 * - The brand-new value is always a valid choice (trimmed).
 */
export function CategoryCombobox({ value, onChange, testId = "category-combobox" }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open || options.length) return;
    api.get("/book/categories")
      .then(({ data }) => setOptions((data || []).map((d) => ({ name: d.name, count: d.count }))))
      .catch(() => {});
  }, [open, options.length]);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const v = value || "";
  const filtered = useMemo(() => {
    if (!v) return options;
    const q = v.toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, v]);

  const exactMatch = options.find((o) => o.name.toLowerCase() === v.toLowerCase());
  const showCreateRow = v.trim() && !exactMatch;

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div className="relative">
        <Input
          value={v}
          onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Type or pick a category…"
          className="bg-[#0a0e1a] border-slate-800 pr-8"
          data-testid={`${testId}-input`}
        />
        <button type="button"
          onClick={() => setOpen((o) => !o)}
          className="absolute right-1.5 top-1.5 p-1 text-slate-500 hover:text-slate-200">
          <ChevronsUpDown className="w-3.5 h-3.5" />
        </button>
      </div>
      {open && (filtered.length > 0 || showCreateRow) && (
        <div className="absolute z-50 mt-1 w-full rounded-md border hairline surface-2 bg-[#0f1626] shadow-xl max-h-[260px] overflow-y-auto"
          data-testid={`${testId}-popover`}>
          {showCreateRow && (
            <button type="button"
              onClick={() => { onChange(v.trim()); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-white/[0.05] flex items-center gap-2 border-b hairline"
              data-testid={`${testId}-create`}>
              <Plus className="w-3.5 h-3.5 text-emerald-400" />
              <span>Create category</span>
              <span className="num text-emerald-300">"{v.trim()}"</span>
            </button>
          )}
          {filtered.map((o) => (
            <button key={o.name} type="button"
              onClick={() => { onChange(o.name); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-white/[0.05] flex items-center gap-2"
              data-testid={`${testId}-option-${o.name}`}>
              <Check className={`w-3.5 h-3.5 ${o.name === v ? "text-blue-400" : "opacity-0"}`} />
              <span className="flex-1">{o.name}</span>
              <span className="text-[10px] num text-slate-500">{o.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Chip-style tag input for Book entries.
 * - `value` is an array of strings; `onChange(nextArray)`.
 * - Type → press Enter / Tab / Comma to add the chip.
 * - Backspace on empty input deletes the last chip.
 * - Suggestions from /book/tags appear in a popover below; click to add.
 */
export function TagInput({ value = [], onChange, testId = "tag-input" }) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open || allTags.length) return;
    api.get("/book/tags")
      .then(({ data }) => setAllTags((data || []).map((d) => d.name)))
      .catch(() => {});
  }, [open, allTags.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const addTag = (raw) => {
    const t = (raw || "").trim().replace(/^#/, "");
    if (!t) return;
    if (value.includes(t)) { setDraft(""); return; }
    onChange([...value, t]);
    setDraft("");
  };

  const removeTag = (t) => onChange(value.filter((x) => x !== t));

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === ",") {
      if (draft.trim()) { e.preventDefault(); addTag(draft); }
    } else if (e.key === "Backspace" && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const suggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return allTags
      .filter((t) => !value.includes(t))
      .filter((t) => !q || t.toLowerCase().includes(q))
      .slice(0, 12);
  }, [allTags, draft, value]);

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div
        className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 rounded-md border border-slate-800 bg-[#0a0e1a] min-h-[38px] focus-within:ring-1 focus-within:ring-blue-500/40 focus-within:border-blue-500/40"
        onClick={() => { setOpen(true); wrapRef.current?.querySelector("input")?.focus(); }}
      >
        {value.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] num bg-blue-500/15 border border-blue-500/30 text-blue-200"
            data-testid={`${testId}-chip-${t}`}>
            {t}
            <button type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(t); }}
              className="text-blue-300 hover:text-white"
              data-testid={`${testId}-chip-remove-${t}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (!open) setOpen(true); }}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          onBlur={() => { /* keep popover open via outside-click guard */ }}
          placeholder={value.length === 0 ? "Add tags — Enter, comma or Tab to confirm" : ""}
          className="flex-1 min-w-[80px] bg-transparent outline-none text-sm placeholder:text-slate-500"
          data-testid={`${testId}-draft`}
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border hairline surface-2 bg-[#0f1626] shadow-xl max-h-[200px] overflow-y-auto"
          data-testid={`${testId}-popover`}>
          {draft.trim() && !allTags.some((t) => t.toLowerCase() === draft.trim().toLowerCase()) && (
            <button type="button"
              onClick={() => addTag(draft)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-white/[0.05] flex items-center gap-2 border-b hairline"
              data-testid={`${testId}-create`}>
              <Plus className="w-3.5 h-3.5 text-emerald-400" />
              <span>Create tag</span>
              <span className="num text-emerald-300">"{draft.trim()}"</span>
            </button>
          )}
          {suggestions.map((t) => (
            <button key={t} type="button"
              onClick={() => addTag(t)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-white/[0.05] flex items-center gap-2"
              data-testid={`${testId}-option-${t}`}>
              <Plus className="w-3 h-3 text-slate-500" />
              <span className="flex-1">{t}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
